import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { ConfirmModal } from '../../../shared/components/ConfirmModal';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type {
  ShopifyComplianceReport,
  ShopifyComplianceRequestDetail,
} from '../../../models/shopifyComplianceRequest';
import {
  completeShopifyComplianceRequest,
  fetchShopifyComplianceReport,
  fetchShopifyComplianceRequest,
  redactShopifyComplianceRequest,
} from '../../../services/admin/shopifyComplianceService';

const REDACTABLE_TOPICS = ['customers/redact', 'shop/redact'];

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function csvCell(value: string | number | null): string {
  const str = value === null || value === undefined ? '' : String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

function buildReportCsv(report: ShopifyComplianceReport): string {
  const header = [
    'SalesOrderId',
    'OrderNumber',
    'Status',
    'ShopifyOrderId',
    'CustomerFullName',
    'CustomerEmail',
    'CustomerPhone',
    'CustomerCompanyName',
    'ShippingAddressLine1',
    'ShippingCity',
    'ShippingState',
    'ShippingPostalCode',
    'ShippingCountry',
    'BillingAddressLine1',
    'BillingCity',
    'BillingState',
    'BillingPostalCode',
    'BillingCountry',
  ];
  const rows = report.orders.map((order) =>
    [
      order.salesOrderId,
      order.orderNumber,
      order.status,
      order.shopifyOrderId,
      order.customerFullName,
      order.customerEmail,
      order.customerPhone,
      order.customerCompanyName,
      order.shippingAddressLine1,
      order.shippingCity,
      order.shippingState,
      order.shippingPostalCode,
      order.shippingCountry,
      order.billingAddressLine1,
      order.billingCity,
      order.billingState,
      order.billingPostalCode,
      order.billingCountry,
    ]
      .map(csvCell)
      .join(',')
  );
  return [header.map(csvCell).join(','), ...rows].join('\n');
}

function downloadReportCsv(report: ShopifyComplianceReport, request: ShopifyComplianceRequestDetail): void {
  const csv = buildReportCsv(report);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `shopify-compliance-report-${request.id}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function ShopifyComplianceRequestDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  useDocumentTitle('Shopify compliance request · Triplicity Backoffice');
  const { getAccessToken } = useAuth();

  const [request, setRequest] = useState<ShopifyComplianceRequestDetail | null>(null);
  const [report, setReport] = useState<ShopifyComplianceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [confirmRedact, setConfirmRedact] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!id) {
      setError('Missing compliance request identifier.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [detail, reportResult] = await Promise.all([
        fetchShopifyComplianceRequest(id, getAccessToken),
        fetchShopifyComplianceReport(id, getAccessToken),
      ]);
      setRequest(detail);
      setReport(reportResult);
    } catch (err) {
      setRequest(null);
      setReport(null);
      setError(err instanceof Error ? err.message : 'Failed to load the compliance request.');
    } finally {
      setLoading(false);
    }
  }, [id, getAccessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRedactConfirm = async (): Promise<void> => {
    if (!id) {
      return;
    }
    await redactShopifyComplianceRequest(id, { retain: false, notes: notes.trim() || undefined }, getAccessToken);
    setStatusMessage('PII masked on the matching Shopify sales orders.');
    setNotes('');
    await load();
  };

  const handleRetain = async (): Promise<void> => {
    if (!id) {
      return;
    }
    if (!notes.trim()) {
      setError('Notes are required to retain a request under legal hold.');
      return;
    }
    setError(null);
    await redactShopifyComplianceRequest(id, { retain: true, notes: notes.trim() }, getAccessToken);
    setStatusMessage('Request retained under legal hold; PII was not redacted.');
    setNotes('');
    await load();
  };

  const handleComplete = async (): Promise<void> => {
    if (!id) {
      return;
    }
    await completeShopifyComplianceRequest(id, { notes: notes.trim() || undefined }, getAccessToken);
    setStatusMessage('Request marked completed.');
    setNotes('');
    await load();
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Shopify compliance request" />
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading compliance request…" />
        </div>
      </div>
    );
  }

  if (error && !request) {
    return (
      <div>
        <PageHeader title="Shopify compliance request" />
        <ErrorAlert message={error} />
      </div>
    );
  }

  if (!request) {
    return (
      <div>
        <PageHeader title="Shopify compliance request" />
        <ErrorAlert message="Compliance request not found." />
      </div>
    );
  }

  const isDataRequest = request.topic === 'customers/data_request';
  const isRedactable = REDACTABLE_TOPICS.includes(request.topic);
  const isActionable = request.status === 'Received';

  return (
    <div>
      <PageHeader
        title={`${request.topic} — ${request.shopDomain}`}
        subtitle="Complete within 30 days of receipt. Redaction masks customer PII on matching Shopify sales orders — it never deletes 3PL operational data (order rows, products, inventory, invoices)."
        actions={
          <Link className="bo-btn bo-btn-secondary" to="/admin/shopify-compliance-requests">
            ← Back to list
          </Link>
        }
      />

      {statusMessage ? (
        <p className="bo-form-success" role="status">
          {statusMessage}
        </p>
      ) : null}

      {error ? <ErrorAlert message={error} /> : null}

      <div className="bo-card">
        <h2 className="bo-admin-section-title">Request</h2>
        <div className="bo-detail-fields-grid">
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Status</span>
            <span className="bo-detail-field-value">{request.status}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Shop id</span>
            <span className="bo-detail-field-value">{request.shopId ?? '—'}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Merchant</span>
            <span className="bo-detail-field-value">
              {request.merchantId ?? 'Unknown shop (integration may have been removed)'}
            </span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Customer id</span>
            <span className="bo-detail-field-value">{request.customerShopifyId ?? '—'}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Customer email</span>
            <span className="bo-detail-field-value">{request.customerEmail ?? '—'}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Customer phone</span>
            <span className="bo-detail-field-value">{request.customerPhone ?? '—'}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Order ids</span>
            <span className="bo-detail-field-value">
              {request.orderIds.length > 0 ? request.orderIds.join(', ') : '—'}
            </span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Received</span>
            <span className="bo-detail-field-value">{formatDateTime(request.receivedAt)}</span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Completed</span>
            <span className="bo-detail-field-value">
              {formatDateTime(request.completedAt)}
              {request.completedBy ? ` by ${request.completedBy}` : ''}
            </span>
          </div>
          <div className="bo-detail-field">
            <span className="bo-detail-field-label">Notes</span>
            <span className="bo-detail-field-value">{request.notes ?? '—'}</span>
          </div>
        </div>
      </div>

      <div className="bo-card">
        <h2 className="bo-admin-section-title">Matched Shopify sales orders</h2>
        {report && report.orders.length === 0 ? (
          <p className="bo-muted">No matching sales orders were found for this request.</p>
        ) : null}
        {report && report.orders.length > 0 ? (
          <div className="bo-admin-table-wrap">
            <table className="bo-admin-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Shipping address</th>
                </tr>
              </thead>
              <tbody>
                {report.orders.map((order) => (
                  <tr key={order.salesOrderId}>
                    <td>{order.orderNumber}</td>
                    <td>{order.status}</td>
                    <td>{order.customerFullName ?? '—'}</td>
                    <td>{order.customerEmail ?? '—'}</td>
                    <td>{order.customerPhone ?? '—'}</td>
                    <td>
                      {[order.shippingAddressLine1, order.shippingCity, order.shippingState, order.shippingPostalCode]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {isDataRequest ? (
        <div className="bo-card">
          <h2 className="bo-admin-section-title">Data request</h2>
          <p className="bo-muted">
            Download the report and send it to the store owner directly — it is never sent to Shopify or the end
            customer. Mark completed once it has been delivered.
          </p>
          <div className="bo-actions">
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              disabled={!report || report.orders.length === 0}
              onClick={() => report && downloadReportCsv(report, request)}
            >
              Download report (CSV)
            </button>
          </div>
          {isActionable ? (
            <div style={{ marginTop: '1rem' }}>
              <label className="bo-field" htmlFor="shopify-compliance-complete-notes">
                <span className="bo-field-label">Notes (optional)</span>
                <textarea
                  id="shopify-compliance-complete-notes"
                  className="bo-input"
                  rows={2}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
              <button type="button" className="bo-btn bo-btn-secondary" onClick={() => void handleComplete()}>
                Mark completed
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {isRedactable && isActionable ? (
        <div className="bo-card">
          <h2 className="bo-admin-section-title">Redact PII</h2>
          <p className="bo-muted">
            This masks customer name, email, phone, company, and shipping/billing address on the matching Shopify
            sales orders. It does not delete the order, line items, inventory history, invoices, or the merchant
            account. If legal retention applies (tax, shipping, contract), retain instead and record why in Notes.
          </p>
          <label className="bo-field" htmlFor="shopify-compliance-redact-notes">
            <span className="bo-field-label">Notes (required to retain)</span>
            <textarea
              id="shopify-compliance-redact-notes"
              className="bo-input"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </label>
          <div className="bo-actions">
            <button type="button" className="bo-btn bo-btn-danger" onClick={() => setConfirmRedact(true)}>
              Execute redaction
            </button>
            <button type="button" className="bo-btn bo-btn-secondary" onClick={() => void handleRetain()}>
              Retain under legal hold
            </button>
          </div>
        </div>
      ) : null}

      <div className="bo-card">
        <h2 className="bo-admin-section-title">Raw payload</h2>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{request.payloadJson}</pre>
      </div>

      <ConfirmModal
        visible={confirmRedact}
        title="Execute redaction"
        message={
          <>
            This masks customer PII (name, email, phone, company, shipping/billing address) on{' '}
            {report ? report.orders.length : 0} matching Shopify sales order(s) for {request.shopDomain}. It does
            not delete 3PL operational data — order rows, line items, inventory history, invoices, and the merchant
            account are unaffected. This cannot be undone.
          </>
        }
        confirmLabel="Redact"
        confirmVariant="danger"
        onClose={() => setConfirmRedact(false)}
        onConfirm={handleRedactConfirm}
      />
    </div>
  );
}
